
import React, { useState, useEffect } from 'react';
import Layout from './components/Layout';
import Auth from './components/Auth';
import Dashboard from './components/Dashboard';
import PatientForm from './components/PatientForm';
import PlanDisplay from './components/PlanDisplay';
import AdminPanel from './components/AdminPanel';
import LandingPage from './components/LandingPage';
import Library from './components/Library';
import History from './components/History';
import PatientManagement from './components/PatientManagement';
import { subscribeToAuthChanges, logout as authLogout } from './services/authService';
import { User, TreatmentPlan, PatientData, ViewState } from './types';
import { gerarPlanoTratamento, getPlanById } from './services/treatmentService';
import { AlertTriangle, Loader2, Sparkles, Zap } from 'lucide-react';

const App: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [showLogin, setShowLogin] = useState(false);
  const [currentView, setCurrentView] = useState<ViewState>('DASHBOARD');
  const [previousView, setPreviousView] = useState<ViewState>('DASHBOARD');
  const [currentPlan, setCurrentPlan] = useState<TreatmentPlan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [publicPlan, setPublicPlan] = useState<TreatmentPlan | null>(null);

  useEffect(() => {
    let isMounted = true;
    
    // Timeout de segurança: Se o Firebase não responder em 4 segundos,
    // forçamos o encerramento do loader para mostrar a Landing Page.
    // Isso evita o erro de "tela preta/branca" no preview da ferramenta.
    const safetyTimeout = setTimeout(() => {
      if (isMounted && loadingInitial) {
        console.warn("Safety timeout: Firebase Auth demorou a responder. Mostrando Landing Page.");
        setLoadingInitial(false);
      }
    }, 4000);

    const init = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const publicPlanId = urlParams.get('p');
      
      if (publicPlanId) {
        try {
          const plan = await getPlanById(publicPlanId);
          if (plan && isMounted) {
            setPublicPlan(plan);
            setLoadingInitial(false);
            clearTimeout(safetyTimeout);
            return;
          }
        } catch (e) {
          console.error("Erro ao carregar plano público:", e);
        }
      }

      const unsubscribe = subscribeToAuthChanges((u: User | null) => {
        if (isMounted) {
          setUser(u);
          setLoadingInitial(false);
          clearTimeout(safetyTimeout);
        }
      });
      
      return unsubscribe;
    };

    init();

    return () => {
      isMounted = false;
      clearTimeout(safetyTimeout);
    };
  }, []);

  const handleNavigate = (view: ViewState) => {
    setPreviousView(currentView);
    setCurrentView(view);
  };

  const handleLogin = (u: User) => {
    setUser(u);
    setCurrentView('DASHBOARD');
  };

  const handleLogout = async () => {
    await authLogout();
    setUser(null);
    setCurrentView('DASHBOARD');
    setCurrentPlan(null);
    setShowLogin(false);
  };

  const handleFormSubmit = async (data: PatientData) => {
    if (!user) return;
    setError(null);
    try {
      const { result, updatedUser } = await gerarPlanoTratamento(data, user);
      setUser(updatedUser);
      if (result.error) setError(result.error);
      if (result.plan) {
        setCurrentPlan(result.plan);
        setPreviousView('FORM');
        setCurrentView('RESULT');
      }
    } catch (e: any) {
      setError("Erro ao gerar protocolo.");
    }
  };

  if (loadingInitial) {
    return (
      <div className="min-h-screen bg-[#130521] flex flex-col items-center justify-center gap-6 p-4">
        <div className="relative">
          <div className="w-20 h-20 border-4 border-purple-900/30 border-t-chroma-accent rounded-full animate-spin"></div>
          <Zap className="absolute inset-0 m-auto text-chroma-accent animate-pulse" size={32} />
        </div>
        <div className="text-center space-y-2">
          <p className="text-white font-black uppercase tracking-[0.4em] text-xs">Cromoterapia Aplicada</p>
          <p className="text-purple-400 text-[10px] font-bold uppercase animate-pulse">Iniciando Portal João Caputo...</p>
        </div>
      </div>
    );
  }

  if (publicPlan) {
    return (
      <Layout user={null} currentView="DASHBOARD" onNavigate={() => {}} onLogout={() => {}}>
         <div className="animate-fade-in py-10">
            <div className="max-w-4xl mx-auto space-y-6">
               <div className="bg-emerald-600 text-white p-6 rounded-3xl shadow-xl flex items-center justify-between no-print">
                  <div className="flex items-center gap-4">
                     <Sparkles size={24} />
                     <div>
                        <p className="text-xs font-black uppercase tracking-widest opacity-80">Visualização de Paciente</p>
                        <p className="text-lg font-bold">Protocolo Recebido!</p>
                     </div>
                  </div>
                  <button 
                    onClick={() => {
                      const url = new URL(window.location.href);
                      url.searchParams.delete('p');
                      window.history.pushState({}, '', url.pathname);
                      setPublicPlan(null);
                    }}
                    className="bg-white/20 hover:bg-white/30 px-4 py-2 rounded-xl transition-all font-bold text-sm"
                  >
                    Área do Aluno
                  </button>
               </div>
               <PlanDisplay plan={publicPlan} onBack={() => setPublicPlan(null)} />
            </div>
         </div>
      </Layout>
    );
  }

  if (!user) {
    return (
      <Layout user={null} currentView="DASHBOARD" onNavigate={() => {}} onLogout={() => {}}>
        {showLogin ? (
          <div className="animate-fade-in">
             <button onClick={() => setShowLogin(false)} className="mb-4 text-purple-400 hover:text-white text-sm flex items-center gap-1 font-bold">
               &larr; Voltar
             </button>
             <Auth onLogin={handleLogin} />
          </div>
        ) : (
          <LandingPage onEnter={() => setShowLogin(true)} user={null} />
        )}
      </Layout>
    );
  }

  return (
    <Layout user={user} currentView={currentView} onNavigate={handleNavigate} onLogout={handleLogout}>
      {error && (
        <div className="fixed top-20 right-4 z-[60] bg-red-500 text-white p-4 rounded-xl shadow-xl animate-bounce flex items-center gap-3 max-w-sm font-bold">
           <AlertTriangle /> {error} <button onClick={() => setError(null)} className="ml-auto">✕</button>
        </div>
      )}

      {currentView === 'DASHBOARD' && (
        <div className="space-y-12">
           <Dashboard 
            user={user} 
            onNew={() => handleNavigate('FORM')} 
            onAdmin={() => handleNavigate('ADMIN')}
            onLibrary={() => handleNavigate('LIBRARY')}
            onHistory={() => handleNavigate('HISTORY')}
            onPatients={() => handleNavigate('PATIENTS')}
          />
          {user.profile === 'Free' && (
             <div className="mt-12 pt-12 border-t border-purple-900/10">
                <LandingPage onEnter={() => {}} user={user} />
             </div>
          )}
        </div>
      )}

      {currentView === 'ADMIN' && <AdminPanel onBack={() => handleNavigate('DASHBOARD')} />}
      {currentView === 'LIBRARY' && <Library onBack={() => handleNavigate('DASHBOARD')} />}

      {currentView === 'HISTORY' && (
        <History 
          user={user} 
          onBack={() => handleNavigate('DASHBOARD')} 
          onSelectPlan={(plan: TreatmentPlan) => {
            setCurrentPlan(plan);
            setPreviousView('HISTORY');
            setCurrentView('RESULT');
          }}
        />
      )}

      {currentView === 'PATIENTS' && (
        <PatientManagement 
          user={user} 
          onBack={() => handleNavigate('DASHBOARD')} 
          onSelectPlan={(plan: TreatmentPlan) => {
            setCurrentPlan(plan);
            setPreviousView('PATIENTS');
            setCurrentView('RESULT');
          }}
        />
      )}

      {currentView === 'FORM' && (
        <div className="animate-fade-in">
          <button onClick={() => handleNavigate('DASHBOARD')} className="mb-4 text-purple-400 hover:text-white text-sm font-bold flex items-center gap-2">
            &larr; Cancelar Atendimento
          </button>
          <PatientForm user={user} onSubmit={handleFormSubmit} />
        </div>
      )}

      {currentView === 'RESULT' && currentPlan && (
        <div className="animate-fade-in">
          <PlanDisplay 
            plan={currentPlan} 
            onBack={() => setCurrentView(previousView === 'FORM' ? 'HISTORY' : previousView)} 
          />
        </div>
      )}
    </Layout>
  );
};

export default App;
